/**
 * Mio AuthFlow — central orchestrator that picks the right card
 * for the current AuthFlow state.
 *
 * Used by:
 *   - MioAuthDialog (modal form factor, wrapped by AuthChrome)
 *   - /sign-in and /sign-up page routes (full-page form factor,
 *     wrapped by (auth)/layout.tsx)
 *
 * No chrome (no X close, no M glyph, no Secured-by-Clerk row) is
 * rendered here — those are owned by the wrapper to keep the dialog
 * and the page route DOM-isomorphic.
 */

"use client";

import { useEffect } from "react";

import { useAuthFlow, type AuthMode } from "./useAuthFlow";
import { SignInForgotPasswordCard } from "./SignInForgotPasswordCard";
import { SignInResetPasswordCard } from "./SignInResetPasswordCard";
import { SignInStartCard } from "./SignInStartCard";
import { SignInVerifyCodeCard } from "./SignInVerifyCodeCard";
import { SignUpContinueUsernameCard } from "./SignUpContinueUsernameCard";
import { SignUpStartCard } from "./SignUpStartCard";
import { SignUpVerifyEmailCard } from "./SignUpVerifyEmailCard";

type AuthFlowProps = {
  mode: AuthMode;
  onAuthenticated: () => void;
  onSwitchMode: (m: AuthMode) => void;
};

export function AuthFlow({
  mode,
  onAuthenticated,
  onSwitchMode,
}: AuthFlowProps) {
  const {
    state,
    goTo,
    setIdentifier,
    setError,
    setSubmitting,
    setVerifyMode,
    markResend,
    reset,
  } = useAuthFlow(mode);

  // If the parent flips mode (e.g. user clicked the foot link), reset
  // to that mode's start step so the right card mounts.
  useEffect(() => {
    reset(mode);
  }, [mode, reset]);

  const shared = {
    flow: state,
    setIdentifier,
    setError,
    setSubmitting,
    setVerifyMode,
    markResend,
    goTo,
  };

  switch (state.step) {
    case "signIn.start":
      return (
        <SignInStartCard
          {...shared}
          onAuthenticated={onAuthenticated}
          onSwitchMode={onSwitchMode}
        />
      );
    case "signIn.verifyCode":
      return (
        <SignInVerifyCodeCard
          {...shared}
          onAuthenticated={onAuthenticated}
        />
      );
    case "signIn.forgotPassword":
      return <SignInForgotPasswordCard {...shared} />;
    case "signIn.resetPassword":
      return (
        <SignInResetPasswordCard
          {...shared}
          onAuthenticated={onAuthenticated}
        />
      );
    case "signUp.start":
      return (
        <SignUpStartCard {...shared} onSwitchMode={onSwitchMode} />
      );
    case "signUp.verifyEmail":
      return (
        <SignUpVerifyEmailCard
          {...shared}
          onAuthenticated={onAuthenticated}
        />
      );
    case "signUp.continueUsername":
      return (
        <SignUpContinueUsernameCard
          {...shared}
          onAuthenticated={onAuthenticated}
        />
      );
    default:
      return null;
  }
}
